import { useMemo, useRef } from 'react';

import type { ThreadRelays } from '@/util/inquiryGroups';

type RelayThread = { conversationId: string; relayedTo?: ThreadRelays };

/**
 * Adds what the threads say about relays to what was already known, without letting a thread
 * that has not loaded its relays erase a name. A thread that reports relays always wins, so a
 * new relay replaces the remembered list as soon as it arrives.
 */
export function mergeKnownRelays(
  known: ReadonlyMap<string, ThreadRelays>,
  threads: readonly (RelayThread | undefined)[]
): Map<string, ThreadRelays> {
  const merged = new Map(known);

  for (const thread of threads) {
    if (thread?.relayedTo !== undefined) {
      merged.set(thread.conversationId, thread.relayedTo);
    }
  }

  return merged;
}

/**
 * The experts each thread of a group was relayed to, remembered across tab switches.
 *
 * The relays come from the open tab's inquiry detail, which reloads when the reader switches tab.
 * Read straight from it, every tab name fell back to "For Ask Anonymously" until the new detail
 * arrived, then jumped back to "For Dr. X". Keeping the last known relays per conversation holds
 * the names steady through the reload.
 */
export function useRememberedRelays(
  threads: readonly (RelayThread | undefined)[]
): ReadonlyMap<string, ThreadRelays> {
  const knownRef = useRef<ReadonlyMap<string, ThreadRelays>>(new Map());

  return useMemo(() => {
    knownRef.current = mergeKnownRelays(knownRef.current, threads);

    return knownRef.current;
  }, [threads]);
}
